import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import Service from "@/services/ElectivePostponementService";

/**
 * Vuex store untuk INM — Penundaan Operasi Elektif.
 */
@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "elective-postponement"
})
class ElectivePostponementModule extends VuexModule {
  // --- State ---
  events: any[] = [];
  currentEvent: any = null;
  reasonDefinitions: any[] = [];
  dashboard: any = null;
  dailyRecap: any = null;
  periodicReports: any[] = [];
  paretoData: any[] = [];
  quarterlyAnalyses: any[] = [];
  allPeriods: any = null;

  loading = false;
  pagination = {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  };

  filters = {
    bulan: new Date().getMonth() + 1,
    tahun: new Date().getFullYear(),
    tanggal: "",
    unit_kamar_operasi: "",
    status: "",
    eksekusi: "",
    dokter_operator_id: null as number | null,
    search: ""
  };

  // --- Mutations ---

  @Mutation SET_EVENTS(val: any[]) {
    this.events = val;
  }

  @Mutation SET_CURRENT_EVENT(val: any) {
    this.currentEvent = val;
  }

  @Mutation SET_REASON_DEFINITIONS(val: any[]) {
    this.reasonDefinitions = val;
  }

  @Mutation SET_DASHBOARD(val: any) {
    this.dashboard = val;
  }

  @Mutation SET_DAILY_RECAP(val: any) {
    this.dailyRecap = val;
  }

  @Mutation SET_PERIODIC_REPORTS(val: any[]) {
    this.periodicReports = val;
  }

  @Mutation SET_PARETO(val: any[]) {
    this.paretoData = val;
  }

  @Mutation SET_QUARTERLY(val: any[]) {
    this.quarterlyAnalyses = val;
  }

  @Mutation SET_ALL_PERIODS(val: any) {
    this.allPeriods = val;
  }

  @Mutation SET_LOADING(val: boolean) {
    this.loading = val;
  }

  @Mutation SET_PAGINATION(val: any) {
    this.pagination = val;
  }

  @Mutation SET_FILTERS(val: any) {
    this.filters = { ...this.filters, ...val };
  }

  @Mutation RESET_FILTERS() {
    this.filters = {
      bulan: new Date().getMonth() + 1,
      tahun: new Date().getFullYear(),
      tanggal: "",
      unit_kamar_operasi: "",
      status: "",
      eksekusi: "",
      dokter_operator_id: null,
      search: ""
    };
  }

  // --- Actions ---

  @Action async fetchReasonDefinitions() {
    const res = await Service.getReasonDefinitions();
    const body = res.data || {};
    this.SET_REASON_DEFINITIONS(body.data || []);
  }

  @Action async fetchEvents(params?: any) {
    this.SET_LOADING(true);
    try {
      const res = await Service.getEvents(params || this.filters);
      const body = res.data || {};
      this.SET_EVENTS(body.data || []);
      if (body.meta) {
        this.SET_PAGINATION({
          currentPage: body.meta.current_page,
          lastPage: body.meta.last_page,
          perPage: body.meta.per_page,
          total: body.meta.total
        });
      }
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action async fetchEvent(id: number) {
    const res = await Service.getEvent(id);
    const data = (res.data && res.data.data) || null;
    this.SET_CURRENT_EVENT(data);
    return data;
  }

  @Action async createEvent(data: any) {
    const res = await Service.createEvent(data);
    return (res.data && res.data.data) || null;
  }

  @Action async updateEvent(payload: { id: number; data: any }) {
    const res = await Service.updateEvent(payload.id, payload.data);
    const data = (res.data && res.data.data) || null;
    this.SET_CURRENT_EVENT(data);
    return data;
  }

  @Action async deleteEvent(id: number) {
    await Service.deleteEvent(id);
  }

  @Action async rescoreEvent(id: number) {
    const res = await Service.rescoreEvent(id);
    const data = (res.data && res.data.data) || null;
    this.SET_CURRENT_EVENT(data);
    return data;
  }

  @Action async fetchDashboard(params: { tanggal?: string; unit_kamar_operasi?: string }) {
    const res = await Service.getDashboard(params);
    const data = (res.data && res.data.data) || null;
    this.SET_DASHBOARD(data);
    return data;
  }

  @Action async fetchDailyRecap(params: { tanggal?: string; unit_kamar_operasi?: string }) {
    const res = await Service.getDailyRecap(params);
    const data = (res.data && res.data.data) || null;
    this.SET_DAILY_RECAP(data);
    return data;
  }

  @Action async signEvent(payload: {
    id: number;
    tier: "petugas" | "ka_instalasi" | "approver";
    signedNama: string;
  }) {
    const res = await Service.signEvent(payload.id, payload.tier, payload.signedNama);
    return (res.data && res.data.data) || null;
  }

  @Action async fetchPeriodic(params?: any) {
    const res = await Service.getPeriodicReports(params);
    const body = res.data || {};
    this.SET_PERIODIC_REPORTS(body.data || []);
    return body;
  }

  @Action async generateMonthly(data: { bulan: number; tahun: number; unit_kamar_operasi?: string }) {
    const res = await Service.generateMonthlyReport(data);
    return (res.data && res.data.data) || null;
  }

  @Action async fetchAllPeriods(params: { tanggal?: string; unit_kamar_operasi?: string }) {
    const res = await Service.getAllPeriods(params);
    const data = (res.data && res.data.data) || null;
    this.SET_ALL_PERIODS(data);
    return data;
  }

  @Action async fetchPareto(params: {
    bulan?: number;
    kuartal?: number;
    tahun: number;
    unit_kamar_operasi?: string;
  }) {
    const res = await Service.getPareto(params);
    const body = res.data || {};
    this.SET_PARETO(body.data || []);
    return body.data || [];
  }

  @Action async fetchQuarterly(params?: any) {
    const res = await Service.getQuarterlyAnalyses(params);
    const body = res.data || {};
    this.SET_QUARTERLY(body.data || []);
    return body.data || [];
  }

  @Action async generateQuarterly(data: {
    kuartal: number;
    tahun: number;
    unit_kamar_operasi?: string;
  }) {
    const res = await Service.generateQuarterlyAnalysis(data);
    return (res.data && res.data.data) || null;
  }
}

export const electivePostponementModule = getModule(ElectivePostponementModule);
